import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Swords, 
  Trophy, 
  Minus,
  Share2, 
  RotateCcw, 
  Home,
  Loader2,
  Flame,
  ThumbsUp,
  Laugh,
  Users,
  Play,
  EyeOff
} from 'lucide-react';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RoomResult = () => {
  const { roomId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    fetchResult();
  }, [roomId]);

  useEffect(() => {
    // Trigger confetti for winner
    if (result && result.winner !== 'draw') {
      const isWinner = 
        (result.winner === 'A' && result.room?.participant_a_id === user?.user_id) ||
        (result.winner === 'B' && result.room?.participant_b_id === user?.user_id);
      
      if (isWinner) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    }
  }, [result, user]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooms/${roomId}/result`);
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to load result');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!user) {
      toast.error('Please login to share');
      return;
    }
    
    setSharing(true);
    try {
      await axios.post(`${API_URL}/api/posts`, {
        content_type: 'debate_result',
        room_id: roomId,
        caption: null
      }, { withCredentials: true });
      
      toast.success('Shared to your profile!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleRematch = () => {
    navigate('/rooms/create');
  };

  const handleHideFromProfile = async () => {
    setHiding(true);
    try {
      await axios.post(`${API_URL}/api/rooms/${roomId}/hide`, {}, { withCredentials: true });
      toast.success('Room hidden from your profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to hide room');
    } finally {
      setHiding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">Result not found</p>
      </div>
    );
  }

  const { room, vote_a_count, vote_b_count, vote_a_percent, vote_b_percent, reactions, winner, result_type } = result;
  
  const isParticipantA = room?.participant_a_id === user?.user_id;
  const isParticipantB = room?.participant_b_id === user?.user_id;
  const isParticipant = isParticipantA || isParticipantB;
  
  const getResultMessage = () => {
    if (winner === 'draw') {
      if (result_type === 'no_engagement') {
        return 'Draw — No Audience Engagement';
      }
      return 'Draw — Close Debate';
    }
    
    const winnerName = winner === 'A' ? room.participant_a_name : room.participant_b_name;
    return `${winnerName} Wins!`;
  };
  
  const getUserResult = () => {
    if (!isParticipant) return null;
    
    if (winner === 'draw') return 'draw';
    if ((winner === 'A' && isParticipantA) || (winner === 'B' && isParticipantB)) {
      return 'win';
    }
    return 'loss';
  };
  
  const userResult = getUserResult();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Result Card */}
        <Card className="bg-white/10 backdrop-blur-lg border-white/20 overflow-hidden">
          {/* Topic Image */}
          {room.topic_image_url && (
            <div className="relative h-48">
              <img 
                src={`${API_URL}${room.topic_image_url}`} 
                alt="Topic"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-xl font-bold text-white">{room.topic}</h2>
              </div>
            </div>
          )}
          
          <CardContent className={`p-6 ${!room.topic_image_url ? 'pt-8' : ''}`}>
            {/* Topic (if no image) */}
            {!room.topic_image_url && (
              <h2 className="text-xl font-bold text-white text-center mb-6">{room.topic}</h2>
            )}
            
            {/* Winner Display */}
            <div className="text-center mb-8">
              <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
                winner === 'draw' 
                  ? 'bg-gray-500/30' 
                  : userResult === 'win' 
                    ? 'bg-yellow-500/30' 
                    : 'bg-red-500/30'
              }`}>
                {winner === 'draw' ? (
                  <Minus className="h-12 w-12 text-gray-400" />
                ) : (
                  <Trophy className={`h-12 w-12 ${userResult === 'win' ? 'text-yellow-500' : 'text-gray-400'}`} />
                )}
              </div>
              
              <h1 className={`text-3xl font-bold mb-2 ${
                winner === 'draw' 
                  ? 'text-gray-400' 
                  : userResult === 'win' 
                    ? 'text-yellow-500' 
                    : 'text-white'
              }`}>
                {getResultMessage()}
              </h1>
              
              {isParticipant && userResult && (
                <p className={`text-lg ${
                  userResult === 'win' ? 'text-green-400' : 
                  userResult === 'loss' ? 'text-red-400' : 'text-gray-400'
                }`}>
                  {userResult === 'win' ? '🎉 You won!' : 
                   userResult === 'loss' ? 'Better luck next time!' : 'It was a close one!'}
                </p>
              )}
            </div>
            
            {/* Participants */}
            <div className="flex items-center justify-center gap-8 mb-8">
              {/* Participant A */}
              <div className={`text-center ${winner === 'A' ? 'scale-110' : ''}`}>
                <div className="relative">
                  <img 
                    src={room.participant_a_picture || '/default-avatar.png'} 
                    alt={room.participant_a_name}
                    className={`w-16 h-16 rounded-full border-4 mx-auto ${
                      winner === 'A' ? 'border-yellow-500' : 'border-blue-500'
                    }`}
                  />
                  {winner === 'A' && (
                    <Trophy className="absolute -top-2 -right-2 h-6 w-6 text-yellow-500" />
                  )}
                </div>
                <p className="font-semibold text-white mt-2">{room.participant_a_name}</p>
                <p className="text-sm text-blue-400">Side A</p>
              </div>
              
              <div className="text-4xl text-gray-500">VS</div>
              
              {/* Participant B */}
              <div className={`text-center ${winner === 'B' ? 'scale-110' : ''}`}>
                <div className="relative">
                  <img 
                    src={room.participant_b_picture || '/default-avatar.png'} 
                    alt={room.participant_b_name}
                    className={`w-16 h-16 rounded-full border-4 mx-auto ${
                      winner === 'B' ? 'border-yellow-500' : 'border-red-500'
                    }`}
                  />
                  {winner === 'B' && (
                    <Trophy className="absolute -top-2 -right-2 h-6 w-6 text-yellow-500" />
                  )}
                </div>
                <p className="font-semibold text-white mt-2">{room.participant_b_name}</p>
                <p className="text-sm text-red-400">Side B</p>
              </div>
            </div>
            
            {/* Vote Stats (Public Rooms) */}
            {room.room_type === 'public' && (vote_a_count > 0 || vote_b_count > 0) && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-400 font-semibold">{vote_a_percent}%</span>
                  <span className="text-gray-400 text-sm flex items-center gap-1">
                    <Users className="h-4 w-4" /> {vote_a_count + vote_b_count} votes
                  </span>
                  <span className="text-red-400 font-semibold">{vote_b_percent}%</span>
                </div>
                <div className="h-4 bg-gray-700 rounded-full overflow-hidden flex">
                  <div 
                    className="bg-blue-500 transition-all duration-500"
                    style={{ width: `${vote_a_percent}%` }}
                  />
                  <div 
                    className="bg-red-500 transition-all duration-500"
                    style={{ width: `${vote_b_percent}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Reactions */}
            {room.room_type === 'public' && reactions && reactions.total > 0 && (
              <div className="flex items-center justify-center gap-6 mb-8">
                <div className="flex items-center gap-2 text-orange-400">
                  <Flame className="h-5 w-5" />
                  <span>{reactions.fire || 0}</span>
                </div>
                <div className="flex items-center gap-2 text-blue-400">
                  <ThumbsUp className="h-5 w-5" />
                  <span>{reactions.thumbs_up || 0}</span>
                </div>
                <div className="flex items-center gap-2 text-yellow-400">
                  <Laugh className="h-5 w-5" />
                  <span>{reactions.laugh || 0}</span>
                </div>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => navigate(`/rooms/${roomId}/replay`)}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                data-testid="view-debate-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                View Debate
              </Button>
              
              {isParticipant && (
                <Button
                  onClick={handleShare}
                  disabled={sharing}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                >
                  {sharing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Share2 className="h-4 w-4 mr-2" />
                  )}
                  Share Result
                </Button>
              )}
              
              <Button
                onClick={handleRematch}
                variant="outline"
                className="flex-1 border-orange-500 text-orange-400 hover:bg-orange-500/20"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Rematch
              </Button>
              
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1 border-white/30 text-white hover:bg-white/10"
              >
                <Home className="h-4 w-4 mr-2" />
                Home
              </Button>
            </div>
            
            {/* Hide from Profile Option */}
            {isParticipant && (
              <div className="mt-4 pt-4 border-t border-white/10 text-center">
                <Button
                  onClick={handleHideFromProfile}
                  disabled={hiding}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-gray-300"
                  data-testid="hide-room-btn"
                >
                  {hiding ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <EyeOff className="h-4 w-4 mr-1" />
                  )}
                  Hide from my profile
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RoomResult;
